import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Clock, Users, MapPin, ChevronLeft, Check, X, AlertCircle, 
  Phone, Share2, Heart, Shirt, Backpack, Shield, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { publicApi } from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1580541631950-7282082b53ce?w=800';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function TourDetailPage() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reservationComplete, setReservationComplete] = useState(false);
  const [reservationData, setReservationData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    persons: '2',
    date: '',
    hotel: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tourRes, settingsRes] = await Promise.all([
          publicApi.getTourBySlug(slug),
          publicApi.getSettings()
        ]);
        setTour(tourRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching tour:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="h-96 bg-stone-200 rounded-3xl skeleton" />
              <div className="space-y-4">
                <div className="h-8 bg-stone-200 rounded skeleton w-3/4" />
                <div className="h-4 bg-stone-200 rounded skeleton w-1/2" />
                <div className="h-32 bg-stone-200 rounded skeleton" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-2xl font-serif text-stone-900 mb-4">Tour no encontrado</h1>
          <Link to="/excursiones">
            <Button>Ver todas las excursiones</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const whatsappNumber = settings?.contact?.whatsapp?.replace(/\D/g, '') || '18091234567';
  
  const handleReserve = async () => {
    setSubmitting(true);
    try {
      // Create reservation in backend
      const response = await publicApi.createReservation({
        tour_id: tour.id,
        tour_name: tour.name,
        tour_price: tour.price,
        customer_name: formData.name,
        customer_phone: formData.phone,
        persons: parseInt(formData.persons) || 2,
        date: formData.date || null,
        hotel: formData.hotel || null
      });
      
      // Store reservation data for invoice download
      setReservationData(response.data);
      setReservationComplete(true);
      toast.success('¡Reserva creada exitosamente!');
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error('Error al crear la reserva. Te redirigimos a WhatsApp.');
      // Fallback to WhatsApp only if reservation fails
      setTimeout(() => openWhatsApp(), 1500);
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = `Hola, Caribbean Groove Tours. Quiero reservar:\n\n` +
      `🏝️ Tour: ${tour.name} ($${tour.price})\n` +
      `👤 Nombre: ${formData.name}\n` +
      `📱 Teléfono: ${formData.phone}\n` +
      `👥 Personas: ${formData.persons}\n` +
      `📅 Fecha: ${formData.date || 'Por definir'}\n` +
      `🏨 Hotel: ${formData.hotel || 'Por definir'}\n` +
      `${reservationData ? `📄 Reserva #: ${reservationData.reservation_number}\n` : ''}` +
      `\n¿Tienen disponibilidad? Gracias.`;
    
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleDownloadInvoice = () => {
    if (reservationData?.id) {
      const invoiceUrl = publicApi.getInvoiceUrl(reservationData.id);
      window.open(invoiceUrl, '_blank');
    }
  };

  const handleCloseModal = () => {
    setShowReserveModal(false);
    setReservationComplete(false);
    setReservationData(null);
    setFormData({
      name: '',
      phone: '',
      persons: '2',
      date: '',
      hotel: ''
    });
  };

  const isFormValid = formData.name && formData.phone;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tour-detail-page">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="pt-28 pb-4 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link 
            to="/excursiones" 
            className="inline-flex items-center text-stone-500 hover:text-primary transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Volver a excursiones
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8 md:py-12 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Gallery */}
            <div className="lg:sticky lg:top-32 lg:self-start overflow-hidden">
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden mb-4 bg-stone-100">
                <img
                  src={getImageUrl(tour.images?.[selectedImage])}
                  alt={tour.name}
                  className="w-full h-64 sm:h-72 md:h-96 lg:h-[500px] object-cover"
                />
                {/* Tags */}
                {tour.tags && tour.tags.length > 0 && (
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {tour.tags.map((tag, i) => (
                      <Badge 
                        key={i} 
                        className={tag === 'Más vendido' ? 'tag-popular' : 'tag-new'}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Thumbnails */}
              {tour.images && tour.images.length > 1 && (
                <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                  {tour.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`gallery-thumb flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden ${
                        selectedImage === i ? 'active ring-2 ring-primary' : ''
                      }`}
                    >
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}

                </div>
              )}
            </div>

            {/* Info */}
            <div>
              {/* Header */}
              <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif text-stone-900 mb-3 break-words">
                  {tour.name}
                </h1>
                <p className="text-stone-600 text-base sm:text-lg leading-relaxed break-words">
                  {tour.short_description}
                </p>
              </div>

              {/* Meta */}
              <div className="flex flex-wrap gap-4 mb-6 p-4 bg-white rounded-2xl border border-stone-100">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="text-stone-700">{tour.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  <span className="text-stone-700">{tour.spots_available} cupos</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-primary">${tour.price}</span>
                  <span className="text-stone-500">/ persona</span>
                </div>
              </div>

              {/* CTA Button - Reservar */}
              <div className="mb-8">
                <Button 
                  size="lg" 
                  onClick={() => setShowReserveModal(true)}
                  className="w-full rounded-xl bg-[#FF6B4A] hover:bg-[#e85a3a] text-white py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  data-testid="tour-reserve-btn"
                >
                  Reservar Ahora
                </Button>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="itinerary" className="w-full">
                <TabsList className="w-full grid grid-cols-4 h-auto p-1 bg-stone-100 rounded-xl">
                  <TabsTrigger value="itinerary" className="rounded-lg py-2 text-xs sm:text-sm">
                    <Calendar className="w-4 h-4 mr-1 hidden sm:inline" />
                    Itinerario
                  </TabsTrigger>
                  <TabsTrigger value="includes" className="rounded-lg py-2 text-xs sm:text-sm">
                    <Check className="w-4 h-4 mr-1 hidden sm:inline" />
                    Incluye
                  </TabsTrigger>
                  <TabsTrigger value="bring" className="rounded-lg py-2 text-xs sm:text-sm">
                    <Backpack className="w-4 h-4 mr-1 hidden sm:inline" />
                    Llevar
                  </TabsTrigger>
                  <TabsTrigger value="info" className="rounded-lg py-2 text-xs sm:text-sm">
                    <AlertCircle className="w-4 h-4 mr-1 hidden sm:inline" />
                    Info
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="itinerary" className="mt-6">
                  <div className="bg-white rounded-2xl p-6 border border-stone-100">
                    <h3 className="font-serif text-xl text-stone-900 mb-4">Itinerario del Día</h3>
                    <div className="space-y-4">
                      {tour.itinerary?.map((item, i) => (
                        <div key={i} className="timeline-item pb-4">
                          <div className="flex items-start gap-3">
                            <span className="font-semibold text-primary min-w-[70px]">
                              {item.time}
                            </span>
                            <span className="text-stone-600">{item.activity}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="includes" className="mt-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-2xl p-6 border border-stone-100">
                      <h3 className="font-serif text-xl text-stone-900 mb-4 flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-500" />
                        Incluye
                      </h3>
                      <ul className="space-y-2">
                        {tour.includes?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-stone-600">
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-white rounded-2xl p-6 border border-stone-100">
                      <h3 className="font-serif text-xl text-stone-900 mb-4 flex items-center gap-2">
                        <X className="w-5 h-5 text-red-500" />
                        No Incluye
                      </h3>
                      <ul className="space-y-2">
                        {tour.not_includes?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-stone-600">
                            <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-1" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="bring" className="mt-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-2xl p-6 border border-stone-100">
                      <h3 className="font-serif text-xl text-stone-900 mb-4 flex items-center gap-2">
                        <Backpack className="w-5 h-5 text-primary" />
                        Qué Llevar
                      </h3>
                      <ul className="space-y-2">
                        {tour.what_to_bring?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-stone-600">
                            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-white rounded-2xl p-6 border border-stone-100">
                      <h3 className="font-serif text-xl text-stone-900 mb-4 flex items-center gap-2">
                        <Shirt className="w-5 h-5 text-primary" />
                        Qué Vestir
                      </h3>
                      <ul className="space-y-2">
                        {tour.what_to_wear?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-stone-600">
                            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="info" className="mt-6">
                  <div className="space-y-4">
                    <div className="bg-white rounded-2xl p-6 border border-stone-100">
                      <h3 className="font-serif text-xl text-stone-900 mb-4 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-primary" />
                        Seguridad y Reglas
                      </h3>
                      <ul className="space-y-2">
                        {tour.safety_rules?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-stone-600">
                            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-white rounded-2xl p-6 border border-stone-100">
                      <h3 className="font-serif text-xl text-stone-900 mb-4 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-primary" />
                        Zonas de Recogida
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {tour.pickup_zones?.map((zone, i) => (
                          <Badge key={i} variant="outline" className="text-stone-600">
                            {zone}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Long Description */}
              {tour.long_description && (
                <div className="mt-8 bg-white rounded-2xl p-6 border border-stone-100">
                  <h3 className="font-serif text-xl text-stone-900 mb-4">Descripción Completa</h3>
                  <p className="text-stone-600 leading-relaxed whitespace-pre-line">
                    {tour.long_description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reserve Modal - Professional Design */}
      <Dialog open={showReserveModal} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden rounded-3xl" aria-describedby="reservation-modal-description">
          <DialogTitle className="sr-only">
            {reservationComplete ? 'Reserva Confirmada' : 'Solicitar Reserva'} - {tour.name}
          </DialogTitle>
          {/* Header with Image */}
          <div className="relative h-32 bg-gradient-to-r from-[#012A36] to-[#00A896]">
            <div className="absolute inset-0 opacity-20">
              <img 
                src={getImageUrl(tour.images?.[0])} 
                alt={tour.name} 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 p-6 h-full flex flex-col justify-end">
              <p className="text-[#00A896] text-sm font-medium mb-1">
                {reservationComplete ? '¡Reserva Creada!' : 'Solicitar Reserva'}
              </p>
              <h3 className="text-white text-xl font-serif font-medium">{tour.name}</h3>
            </div>
          </div>
          
          <div className="p-6 space-y-5">
            {!reservationComplete ? (
              <>
                {/* Price Summary */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#FAFAF8] to-white rounded-2xl border border-stone-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#FF6B4A]/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#FF6B4A]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#4A6B74]">Precio por persona</p>
                      <p className="text-2xl font-bold text-[#012A36]">${tour.price}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#4A6B74]">Duración</p>
                    <p className="font-semibold text-[#012A36]">{tour.duration}</p>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="reserve-name" className="text-[#012A36] font-medium">
                      Nombre completo <span className="text-[#FF6B4A]">*</span>
                    </Label>
                    <Input
                      id="reserve-name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="¿Cómo te llamas?"
                      className="mt-2 rounded-xl border-stone-200 focus:border-[#00A896] focus:ring-[#00A896]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="reserve-phone" className="text-[#012A36] font-medium">
                      Teléfono de contacto <span className="text-[#FF6B4A]">*</span>
                    </Label>
                    <Input
                      id="reserve-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+1 809 555 1234"
                      className="mt-2 rounded-xl border-stone-200 focus:border-[#00A896] focus:ring-[#00A896]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#012A36] font-medium">Personas</Label>
                      <Select 
                        value={formData.persons} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, persons: v }))}
                      >
                        <SelectTrigger className="mt-2 rounded-xl border-stone-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6,7,8,9,10].map(n => (
                            <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'persona' : 'personas'}</SelectItem>
                          ))}
                          <SelectItem value="10+">Más de 10</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="reserve-date" className="text-[#012A36] font-medium">Fecha deseada</Label>
                      <Input
                        id="reserve-date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                        className="mt-2 rounded-xl border-stone-200 focus:border-[#00A896] focus:ring-[#00A896]"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="reserve-hotel" className="text-[#012A36] font-medium">Hotel o zona de recogida</Label>
                    <Input
                      id="reserve-hotel"
                      value={formData.hotel}
                      onChange={(e) => setFormData(prev => ({ ...prev, hotel: e.target.value }))}
                      placeholder="Ej: Hard Rock Hotel, Punta Cana"
                      className="mt-2 rounded-xl border-stone-200 focus:border-[#00A896] focus:ring-[#00A896]"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleReserve}
                  disabled={!isFormValid || submitting}
                  className="w-full rounded-xl bg-[#00A896] hover:bg-[#008f80] text-white py-6 text-lg font-semibold transition-all duration-300 disabled:opacity-50"
                >
                  {submitting ? 'Procesando...' : 'Solicitar Reserva'}
                </Button>

                {/* Footer Note */}
                <p className="text-center text-xs text-[#4A6B74]">
                  Te contactaremos en menos de 2 horas para confirmar tu reserva
                </p>
              </>
            ) : (
              /* Confirmation View */
              <div className="space-y-5">
                {/* Success Icon */}
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-[#00A896]/10 flex items-center justify-center">
                    <Check className="w-8 h-8 text-[#00A896]" />
                  </div>
                </div>

                {/* Reservation Details */}
                <div className="text-center space-y-2">
                  <p className="text-sm text-[#4A6B74]">Número de Reserva</p>
                  <p className="text-2xl font-bold text-[#012A36]">{reservationData?.reservation_number}</p>
                </div>

                {/* Summary Card */}
                <div className="bg-gradient-to-r from-[#FAFAF8] to-white rounded-2xl border border-stone-100 p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[#4A6B74]">Tour</span>
                    <span className="font-medium text-[#012A36]">{tour.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#4A6B74]">Personas</span>
                    <span className="font-medium text-[#012A36]">{formData.persons}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#4A6B74]">Fecha</span>
                    <span className="font-medium text-[#012A36]">{formData.date || 'Por confirmar'}</span>
                  </div>
                  <div className="border-t border-stone-200 pt-3 flex justify-between items-center">
                    <span className="text-[#4A6B74] font-semibold">Total</span>
                    <span className="text-xl font-bold text-[#FF6B4A]">${reservationData?.total_price} USD</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={handleDownloadInvoice}
                    className="w-full rounded-xl bg-[#012A36] hover:bg-[#023647] text-white py-5 text-base font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="download-invoice-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Descargar Factura PDF
                  </Button>

                  <Button
                    onClick={openWhatsApp}
                    className="w-full rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white py-5 text-base font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="whatsapp-confirm-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Confirmar por WhatsApp
                  </Button>
                </div>

                {/* Info Note */}
                <p className="text-center text-xs text-[#4A6B74]">
                  Descarga tu factura y contacta por WhatsApp para confirmar disponibilidad
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
